import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { TreasureItem } from '../../types';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';

interface ManageVaultModalProps {
  open: boolean;
  onClose: () => void;
}

type ItemCategory = 'general' | 'scrolls' | 'potions' | 'grimoires';

const CATEGORIES: { key: ItemCategory; label: string }[] = [
  { key: 'general', label: 'Magic Items' },
  { key: 'scrolls', label: 'Scrolls' },
  { key: 'potions', label: 'Potions' },
  { key: 'grimoires', label: 'Grimoires' },
];

function categoriseItem(item: TreasureItem): ItemCategory {
  const id = item.id.toLowerCase();
  if (id.startsWith('scroll_')) return 'scrolls';
  if (id.startsWith('grimoire_')) return 'grimoires';
  if (item.baseItemType === 'potion') return 'potions';
  return 'general';
}

const VaultListItem = React.memo<{
  item: TreasureItem;
  addCount: number;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
}>(({ item, addCount, onAdd, onRemove }) => (
  <ListItem
    secondaryAction={
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {addCount > 0 && (
          <>
            <IconButton size="small" onClick={() => onRemove(item.id)}>
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>
              {addCount}
            </Typography>
          </>
        )}
        <IconButton size="small" onClick={() => onAdd(item.id)} color="primary">
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
    }
  >
    <ListItemText
      primary={item.name}
      secondary={
        <Box component="span">
          {item.salePrice > 0 && (
            <Typography variant="caption" component="span">
              Sale: {item.salePrice}gc
            </Typography>
          )}
          {item.purchasePrice != null && item.purchasePrice > 0 && (
            <Typography variant="caption" component="span" sx={{ ml: 1 }}>
              Buy: {item.purchasePrice}gc
            </Typography>
          )}
        </Box>
      }
    />
  </ListItem>
));

export const ManageVaultModal: React.FC<ManageVaultModalProps> = React.memo(({
  open,
  onClose,
}) => {
  const vaultItems = useWarbandStore((s) => s.vaultItems);
  const gold = useWarbandStore((s) => s.warbands.find((w) => w.id === s.selectedWarbandId)?.gold ?? 0);
  const updateVaultItems = useDirtyTrackingStore((s) => s.updateVaultItems);
  const rawTreasureItems = useReferenceDataStore((s) => s.treasureItems);
  const allTreasureItems = React.useMemo(
    () => [...rawTreasureItems].sort((a, b) => a.name.localeCompare(b.name)),
    [rawTreasureItems]
  );
  const [itemsToAdd, setItemsToAdd] = useState<Map<string, number>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [isFree, setIsFree] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setItemsToAdd(new Map());
    setError(null);
    setIsFree(false);
  }, [open]);

  const totalToAdd = React.useMemo(() => {
    let total = 0;
    itemsToAdd.forEach((count) => { total += count; });
    return total;
  }, [itemsToAdd]);

  const totalPurchaseCost = React.useMemo(() => {
    let cost = 0;
    itemsToAdd.forEach((count, id) => {
      const item = allTreasureItems.find((t) => t.id === id);
      if (item?.purchasePrice != null) {
        cost += item.purchasePrice * count;
      }
    });
    return cost;
  }, [itemsToAdd, allTreasureItems]);

  const canAfford = isFree || totalPurchaseCost === 0 || gold >= totalPurchaseCost;

  const handleClose = () => {
    setFilter('');
    setError(null);
    setActiveTab(0);
    setIsFree(false);
    onClose();
  };

  const handleSave = () => {
    const existingIds = vaultItems.map((v) => v.id);
    const newIds: string[] = [];
    itemsToAdd.forEach((count, id) => {
      for (let i = 0; i < count; i++) {
        newIds.push(id);
      }
    });
    updateVaultItems([...existingIds, ...newIds], isFree ? 0 : totalPurchaseCost, isFree);
    handleClose();
  };

  const addItem = useCallback((itemId: string) => {
    setItemsToAdd((prev) => {
      const next = new Map(prev);
      next.set(itemId, (next.get(itemId) ?? 0) + 1);
      return next;
    });
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setItemsToAdd((prev) => {
      const next = new Map(prev);
      const count = next.get(itemId) ?? 0;
      if (count <= 1) {
        next.delete(itemId);
      } else {
        next.set(itemId, count - 1);
      }
      return next;
    });
  }, []);

  const categorisedItems = React.useMemo(() => {
    const lower = filter.trim().toLowerCase();
    const filtered = lower
      ? allTreasureItems.filter(
          (item) =>
            item.name.toLowerCase().includes(lower) ||
            (item.description?.toLowerCase().includes(lower) ?? false)
        )
      : allTreasureItems;

    const groups: Record<ItemCategory, TreasureItem[]> = {
      general: [],
      scrolls: [],
      potions: [],
      grimoires: [],
    };
    for (const item of filtered) {
      groups[categoriseItem(item)].push(item);
    }
    return groups;
  }, [allTreasureItems, filter]);

  const activeCategory = CATEGORIES[activeTab]?.key ?? 'general';
  const displayedItems = categorisedItems[activeCategory];

  const renderedList = React.useMemo(() => {
    if (displayedItems.length === 0) {
      return (
        <Box sx={{ height: 340, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No items in this category{filter ? ' matching the filter' : ''}.
          </Typography>
        </Box>
      );
    }
    return (
      <List
        dense
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          height: 340,
          overflowY: 'auto',
          py: 0,
        }}
      >
        {displayedItems.map((item) => (
          <VaultListItem
            key={item.id}
            item={item}
            addCount={itemsToAdd.get(item.id) ?? 0}
            onAdd={addItem}
            onRemove={removeItem}
          />
        ))}
      </List>
    );
  }, [displayedItems, itemsToAdd, addItem, removeItem, filter]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add to Vault</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {allTreasureItems.length > 0 && (
          <>
            <TextField
              size="small"
              placeholder="Filter items..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              fullWidth
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" color={!canAfford ? 'error' : 'textSecondary'}>
              {totalToAdd} item{totalToAdd !== 1 ? 's' : ''} to add
              {totalPurchaseCost > 0 && ` · Cost: ${totalPurchaseCost}gc`}
              {!canAfford && ` (have ${gold}gc)`}
            </Typography>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
              >
                {CATEGORIES.map((cat) => (
                  <Tab key={cat.key} label={cat.label} />
                ))}
              </Tabs>
            </Box>
            {renderedList}
          </>
        )}

        {allTreasureItems.length === 0 && !error && (
          <Typography variant="body2" color="text.secondary">
            No treasure items available.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {totalPurchaseCost > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isFree}
                onChange={(e) => setIsFree(e.target.checked)}
                size="small"
                disabled={totalToAdd === 0}
              />
            }
            label={<Typography variant="body2">Add for free</Typography>}
            sx={{ mr: 'auto' }}
          />
        )}
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={totalToAdd === 0 || !canAfford}
        >
          Add to Vault
        </Button>
      </DialogActions>
    </Dialog>
  );
});
